'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  decideBusinessRevenueValidationCase,
  getBusinessRevenueValidationCases,
  type BusinessRevenueValidationCase,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';
import { errorMessage } from './BusinessReportFinanceUi';

export function BusinessRevenueValidationTab({
  active,
  token,
  canDecide,
}: {
  active: boolean;
  token?: string;
  canDecide: boolean;
}) {
  const [cases, setCases] = useState<BusinessRevenueValidationCase[]>([]);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      setCases(await getBusinessRevenueValidationCases(token));
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load validation cases', message: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }, [token]);

  useEffect(() => { if (active) void load(); }, [active, load]);

  const visibleCases = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return cases;
    return cases.filter((item) => [
      item.caseReference, item.exceptionCode, item.dataSource.sourceName,
      item.center.displayName, item.presentation.transactionReference,
      item.presentation.detail, item.presentation.exceptionLabel,
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [cases, search]);

  if (!active) return null;

  const decide = async (item: BusinessRevenueValidationCase, action: string) => {
    const reason = (reasons[item.id] || '').trim();
    if (!reason) {
      setAlert({ type: 'warning', title: 'Decision note required', message: 'Enter a reason or acknowledgment note before recording the decision.' });
      return;
    }
    setBusy(true);
    try {
      await decideBusinessRevenueValidationCase(item.id, action, reason, token);
      setReasons((current) => ({ ...current, [item.id]: '' }));
      await load();
      setAlert({ type: 'success', title: 'Decision recorded', message: 'The validation case and its audit history have been updated.' });
    } catch (error) {
      setAlert({ type: 'error', title: 'Validation decision failed', message: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  return <section className="business-source-panel" aria-label="Revenue validation and discrepancies">
    {busy && <Loader message="Loading validation cases..." />}
    <div className="enterprise-card enterprise-table-card business-source-table-card revenue-validation-card">
      <div className="revenue-validation-filter">
        <label className="business-source-field"><span>Search cases</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Case, source, centre or reference" /></label>
        <button type="button" className="btn revenue-operation-filter-refresh" onClick={() => void load()}>Refresh</button>
      </div>
      <div className="enterprise-card-heading"><div><strong>Revenue validation queue</strong><span>Review the source transaction and the exact value change before deciding.</span></div><span className="business-source-count">{visibleCases.length} open</span></div>
      <div className="enterprise-table-wrap"><table className="enterprise-table revenue-validation-table"><thead><tr><th>Case / Source</th><th>Centre / Date</th><th>Transaction / Details</th><th>What changed</th><th>Current assignment</th><th>Decision</th></tr></thead><tbody>
        {visibleCases.length ? visibleCases.map((item) => {
          const activeStep = item.steps.find((step) => ['ACTIVE', 'NEEDS_INFORMATION'].includes(step.status));
          const comparison = item.presentation.financialComparison;
          const canAct = canDecide && item.decisionAccess.allowed;
          return <tr key={item.id}><td><strong>{item.presentation.exceptionLabel}</strong><span className="business-source-secondary">{item.dataSource.sourceName}</span></td><td>{item.center.displayName}<span className="business-source-secondary">{item.businessDate.slice(0, 10)}</span></td><td><strong>{item.presentation.referenceLabel} {item.presentation.transactionReference}</strong><span className="business-source-secondary">{item.presentation.detailLabel}: {item.presentation.detail}</span></td><td>{comparison ? comparison.changedFields.length ? comparison.changedFields.map((field) => <span key={field.key} className="business-source-secondary"><strong>{field.label}:</strong>{' '}{formatAmount(field.current, comparison.currency)}{' → '}{formatAmount(field.proposed, comparison.currency)}</span>) : <><strong>Financial amounts unchanged</strong><span className="business-source-secondary">Other source details changed.</span></> : <><strong>{formatAmount(item.signedAmount, item.currency)}</strong><span className="business-source-secondary">Reported signed amount</span></>}</td><td>{activeStep ? <>{activeStep.stepName}<span className="business-source-secondary">{friendlyAssignee(activeStep.assigneeType, activeStep.assigneeKey)}</span></> : 'No active review step'}<span className="business-source-secondary">{friendlyStatus(item.status)}</span></td><td>{canAct ? <><input aria-label={`Decision reason for ${item.caseReference}`} placeholder="Required reason / note" value={reasons[item.id] || ''} onChange={(event) => setReasons({ ...reasons, [item.id]: event.target.value })} />{item.decisionAccess.mode === 'ORPHAN_RECOVERY' && <span className="business-source-secondary">The configured assignment has no active users. This decision will be recorded as an authorized recovery.</span>}<div className="business-source-actions">{item.severity === 'INFORMATIONAL' ? <button className="btn btn-outline btn-sm" onClick={() => void decide(item, 'ACKNOWLEDGED')}>Acknowledge</button> : <><button className="btn btn-primary btn-sm" onClick={() => void decide(item, 'APPROVED')}>Approve</button><button className="btn btn-outline btn-sm" onClick={() => void decide(item, 'REJECTED')}>Reject</button><button className="btn btn-outline btn-sm" onClick={() => void decide(item, 'REQUESTED_INFORMATION')}>Request info</button></>}</div></> : <span className="business-source-secondary">{item.decisionAccess.message}</span>}</td></tr>;
        }) : <tr><td colSpan={6} className="empty-cell">No open validation cases.</td></tr>}
      </tbody></table></div>
    </div>
    {alert && <AdminAlertModal {...alert} onClose={() => setAlert(null)} />}
  </section>;
}

function formatAmount(value: string, currency: string) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return `${currency} ${value}`;
  return `${currency} ${new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)}`;
}

function friendlyAssignee(type: string, key: string) {
  const name = key
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
  return type === 'ROLE' ? `Assigned to ${name} role` : `Assigned to ${name}`;
}

function friendlyStatus(status: string) {
  const labels: Record<string, string> = {
    PENDING: 'Awaiting review',
    IN_REVIEW: 'Review in progress',
    NEEDS_INFORMATION: 'More information requested',
  };
  return labels[status] || status;
}
